import { useEffect, useState } from "react";
import { Download } from "lucide-react";

import { api } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog.js";
import { Skeleton, SkeletonRegion } from "../ui/skeleton.js";
import type { SlotDoc } from "./DocumentSlot.js";

type PreviewKind = "image" | "pdf" | "other";

/** What the browser can show inline. The stored type is best; the file name decides when it is missing. */
function previewKind(doc: SlotDoc): PreviewKind {
  const type = (doc.mimeType ?? "").toLowerCase();
  const ext = doc.name.includes(".") ? doc.name.split(".").pop()!.toLowerCase() : "";
  if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  return "other";
}

/**
 * Shows an uploaded document in a dialog: the image or PDF itself, with a skeleton in its place
 * while the link is fetched and while the file loads. A file the browser can't show (Word) gets a
 * download button instead. The link is a short-lived signed URL, fetched each time it opens.
 */
export function DocumentPreviewDialog({ doc, open, onOpenChange }: {
  doc: SlotDoc;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  /** The image / PDF has finished loading, so the skeleton can go. */
  const [loaded, setLoaded] = useState(false);
  const kind = previewKind(doc);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUrl(null); setFailed(false); setLoaded(false);
    api.get<{ url: string }>(`/documents/${doc.id}/url`)
      .then(({ data }) => { if (!cancelled) setUrl(data.url); })
      .catch((err) => { logError("DocumentPreviewDialog.loadUrl", err, { docId: doc.id }); if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [open, doc.id]);

  const loading = !failed && (url === null || (kind !== "other" && !loaded));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate" title={doc.name}>{doc.name}</DialogTitle>
          <DialogDescription>Preview of the file you uploaded.</DialogDescription>
        </DialogHeader>

        <div className="relative min-h-[50vh]">
          {loading && (
            <SkeletonRegion label="Loading the document" className="absolute inset-0">
              <Skeleton className="h-full w-full" />
            </SkeletonRegion>
          )}

          {failed && <p className="text-[13px] text-[var(--color-error-fg)]">The file could not be loaded. Please close this and try again.</p>}

          {/* Hidden (not unmounted) until loaded, so the browser fetches it while the skeleton shows. */}
          {url && kind === "image" && (
            <img
              src={url}
              alt={doc.name}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
              className={cn("mx-auto max-h-[70vh] w-auto max-w-full rounded-md border border-surface-200 object-contain", !loaded && "invisible")}
            />
          )}
          {url && kind === "pdf" && (
            <iframe
              src={url}
              title={doc.name}
              onLoad={() => setLoaded(true)}
              className={cn("h-[70vh] w-full rounded-md border border-surface-200", !loaded && "invisible")}
            />
          )}
          {url && kind === "other" && (
            <div className="flex flex-col items-start gap-3">
              <p className="text-[13px] text-surface-600">This kind of file can't be shown here. Download it to open it.</p>
              <Button asChild variant="outline" size="sm">
                <a href={url} target="_blank" rel="noopener noreferrer"><Download size={14} /> Download</a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
